/**
 * Utilitaires de formatage de montants, prix et nombres
 * Fonctions pures < 30 lignes chacune
 */

/**
 * Formate un montant en euros (1 234,56 €)
 * @param {number|string} amount - Montant à formater
 * @param {Object} options - Options de formatage
 * @returns {string} - Montant formaté
 */
export function formatCurrency(amount, options = {}) {
  const {
    locale = 'fr-FR',
    currency = 'EUR',
    minimumFractionDigits = 2,
    maximumFractionDigits = 2
  } = options

  const num = typeof amount === 'string' ? parseFloat(amount) : amount
  if (isNaN(num) || num === null || num === undefined) {
    return options.showZero !== false && num === 0 ? '0,00 €' : ''
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits,
    maximumFractionDigits
  }).format(num)
}

/**
 * Alias de formatCurrency pour compatibilité
 * @param {number|string} amount - Montant à formater
 * @returns {string} - Montant formaté
 */
export function formatAmount(amount) {
  if (!amount) return 'N/A'
  return formatCurrency(amount)
}

/**
 * Alias de formatCurrency pour les prix
 * @param {number|string} price - Prix à formater
 * @param {Object} options - Options de formatage
 * @returns {string} - Prix formaté
 */
export function formatPrice(price, options = {}) {
  return formatCurrency(price, options)
}

/**
 * Formate un nombre avec séparateurs (1 234,56)
 * @param {number|string} number - Nombre à formater
 * @param {Object} options - Options de formatage
 * @returns {string} - Nombre formaté
 */
export function formatNumber(number, options = {}) {
  const {
    locale = 'fr-FR',
    minimumFractionDigits = 0,
    maximumFractionDigits = 2
  } = options

  const num = typeof number === 'string' ? parseFloat(number) : number
  if (isNaN(num)) return ''

  return new Intl.NumberFormat(locale, {
    minimumFractionDigits,
    maximumFractionDigits
  }).format(num)
}

/**
 * Graduation d'un axe en euros : « 48 k € » se lit de loin là où « 48 200 € »
 * demande de compter les zéros. Une décimale : arrondi à l'entier, 1 000,
 * 1 200 et 1 400 s'écrivaient tous « 1 k € ».
 * @param {number} valeur - Montant en euros
 * @returns {string} - Graduation formatée
 */
export function formatAxisEuros(valeur) {
  return valeur >= 1000
    ? `${formatNumber(valeur / 1000, { maximumFractionDigits: 1 })} k €`
    : `${valeur} €`
}
